import { Injectable } from '@angular/core';
import { BaseDAO } from './baseDAO';
import { ConstantMan } from '../constantMan';
import { ApiHelper } from '../apiHelper';
import { Observable } from 'rxjs/Rx';
import { HttpHeaders } from '@angular/common/http';
import { PaymentMethod } from '@stripe/stripe-js';
import { PaymentIntent } from '@stripe/stripe-js/types/api/payment-intents';
import { Charter } from 'anchor-shared/lib/model/entity/charter';
import { Customer } from 'anchor-shared/lib/model/entity/customer';

@Injectable()
export class StripePaymentDAO extends BaseDAO<string> {
  constructor(public readonly apiHelper: ApiHelper) {
    super(apiHelper, ConstantMan.API.RESOURCE.STRIPE_PAYMENTS);
  }

  public listCustomerPaymentMethods(
    date: string,
  ): Observable<PaymentMethodData> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      `/list-payment-methods/`;
    const method = 'PUT';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {
      date: date,
    };
    return this.apiHelper.apiCall<PaymentMethodData>(
      url,
      method,
      null,
      data,
      headers,
    );
  }

  public retryPaymentIntent(
    paymentIntent: string,
  ): Observable<{ paymentIntent: PaymentIntent; date: string }> {
    const url: string = `${this.apiHelper.getServiceName()}${this.getResource()}/retry-payment-intent/${paymentIntent}`;
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = null;
    return this.apiHelper.apiCall<{
      paymentIntent: PaymentIntent;
      date: string;
    }>(url, method, null, data, headers);
  }

  public adminListCustomerPaymentMethods(
    userId: number,
  ): Observable<PaymentMethodData> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      `/admin-list-payment-methods/${userId}`;
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = null;
    return this.apiHelper.apiCall<PaymentMethodData>(
      url,
      method,
      null,
      data,
      headers,
    );
  }

  public cancelPaymentIntent(paymentIntentId: string, isHold = false) {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      `/cancel-payment-intent/`;
    const method = 'PUT';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {
      paymentIntentId: paymentIntentId,
      isHold: isHold,
    };
    return this.apiHelper.apiCall<PaymentMethod>(
      url,
      method,
      null,
      data,
      headers,
    );
  }

  public captureFunds(paymentIntentId: string, stripeCustomerId: string) {
    const url: string =
      this.apiHelper.getServiceName() + this.getResource() + `/capture-funds/`;
    const method = 'PUT';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {
      paymentIntentId: paymentIntentId,
      stripeCustomerId: stripeCustomerId,
    };
    return this.apiHelper.apiCall<any>(url, method, null, data, headers);
  }

  refundHold(paymentIntentId: string, amount: number) {
    const url: string =
      this.apiHelper.getServiceName() + this.getResource() + `/refund-hold/`;
    const method = 'PUT';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {
      paymentIntentId: paymentIntentId,
      amount: amount,
    };
    return this.apiHelper.apiCall<any>(url, method, null, data, headers);
  }

  public placeHold(charter: Partial<Charter>) {
    const url: string =
      this.apiHelper.getServiceName() + this.getResource() + `/place-hold/`;
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {
      charter: charter,
    };
    return this.apiHelper.apiCall<PaymentMethod>(
      url,
      method,
      null,
      data,
      headers,
    );
  }

  public detachPaymentMethod(paymentMethodId: string) {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      `/detach-payment-method/`;
    const method = 'DELETE';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {
      paymentMethodId: paymentMethodId,
    };
    return this.apiHelper.apiCall<PaymentMethod>(
      url,
      method,
      null,
      data,
      headers,
    );
  }

  public setDefaultPaymentMethod(paymentMethodId: string) {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      `/set-default-payment-method`;
    const method = 'PUT';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {
      paymentMethodId: paymentMethodId,
    };
    return this.apiHelper.apiCall<PaymentMethod>(
      url,
      method,
      null,
      data,
      headers,
    );
  }

  public getStripeCustomerByUserId() {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      `/get-stripe-customer`;
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<PaymentMethod>(
      url,
      method,
      null,
      null,
      headers,
    );
  }

  public getIdentityVerification() {
    const url: string = `${this.apiHelper.getServiceName()}${this.getResource()}/identity-verification`;
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<any>(url, method, null, null, headers);
  }

  createHoldForBoatChange(paymentIntentId: string, charter: Charter) {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      `/security-hold/boat-change/`;
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {
      paymentIntentId: paymentIntentId,
      charter: charter,
    };
    return this.apiHelper.apiCall<PaymentMethod>(
      url,
      method,
      null,
      data,
      headers,
    );
  }
}
